import React, { useState } from 'react';
import { Check, Eye, EyeOff, Lock, Minus } from 'lucide-react';

import RequiredAsteriskTooltip from './RequiredAsteriskTooltip';
import {
  PASSWORD_RULES,
  evaluatePassword,
} from '../../../utils/passwordStrength';

interface PasswordFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  error?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  /** Muestra medidor de fuerza + checklist debajo del input. */
  showStrength?: boolean;
  autoComplete?: string;
}

const SEGMENT_COLORS = [
  'bg-danger',
  'bg-danger',
  'bg-warning',
  'bg-warning',
  'bg-success',
];

const PasswordField: React.FC<PasswordFieldProps> = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  error,
  placeholder,
  required,
  disabled,
  showStrength = false,
  autoComplete = 'new-password',
}) => {
  const [visible, setVisible] = useState(false);

  const evaluation = evaluatePassword(value);
  const segments = 5;

  return (
    <div className="mb-5 w-full">
      <label
        htmlFor={name}
        className="mb-2.5 flex items-center gap-1 text-sm font-medium text-black dark:text-white"
      >
        {label}
        {required && <RequiredAsteriskTooltip delay={400} />}
      </label>

      <div
        className={`
          bg-gray-1 flex items-stretch overflow-hidden rounded-xl border border-stroke
          transition-all duration-200 focus-within:border-primary focus-within:ring-2
          focus-within:ring-primary/10 dark:border-strokedark dark:bg-meta-4
          ${disabled ? 'cursor-not-allowed opacity-70' : ''}
        `}
      >
        <div className="flex items-center justify-center border-r border-stroke bg-gray-2 px-4 dark:border-strokedark dark:bg-boxdark-2">
          <Lock className="h-4 w-4 text-body dark:text-bodydark2" />
        </div>

        <input
          id={name}
          name={name}
          type={visible ? 'text' : 'password'}
          value={value ?? ''}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          className="w-full bg-transparent px-4 py-3.5 text-sm text-black outline-none placeholder:text-body dark:text-white dark:placeholder:text-bodydark2"
        />

        <button
          type="button"
          tabIndex={-1}
          onClick={() => setVisible((v) => !v)}
          disabled={disabled}
          aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          className="flex items-center justify-center px-4 text-body transition hover:text-primary disabled:pointer-events-none dark:text-bodydark2"
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>

      {error && <p className="mt-2 text-sm text-danger">{error}</p>}

      {showStrength && value.length > 0 && (
        <div className="mt-3 space-y-2.5">
          <div className="flex items-center gap-2">
            <div className="flex flex-1 gap-1">
              {Array.from({ length: segments }).map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${
                    i < evaluation.score
                      ? SEGMENT_COLORS[evaluation.score - 1]
                      : 'bg-stroke dark:bg-strokedark'
                  }`}
                />
              ))}
            </div>
            <span className="w-20 text-right text-xs font-medium text-body dark:text-bodydark2">
              {evaluation.label}
            </span>
          </div>

          <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            {PASSWORD_RULES.map((rule) => {
              const ok = evaluation.checks[rule.key];
              return (
                <li
                  key={rule.key}
                  className={`flex items-center gap-1.5 text-xs ${
                    ok ? 'text-success' : 'text-body dark:text-bodydark2'
                  }`}
                >
                  {ok ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <Minus className="h-3.5 w-3.5" />
                  )}
                  {rule.label}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PasswordField;
